import React, { useState, useEffect } from 'react';
import AuthContext from './AuthContext';
import { toast } from 'react-toastify';
import AuthService from '../services/authService';

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  
  // Check if user is already authenticated on mount
  useEffect(() => {
    const verifyToken = async () => {
      const storedToken = localStorage.getItem('token');
      
      if (storedToken) {
        // Parse the JWT token to extract user information
        try {
          // Decode the JWT token (without validation)
          // This is just to extract the user info stored in the token
          const tokenParts = storedToken.split('.');
          if (tokenParts.length === 3) {
            const payload = JSON.parse(atob(tokenParts[1]));
            
            // Check if token is expired
            if (payload.exp && payload.exp * 1000 < Date.now()) {
              console.log('Token expired');
              localStorage.removeItem('token');
              setUser(null);
              setToken(null);
            } else {
              // Use the user info from the token
              // Make sure all user properties are preserved, including role
              let userData = payload.user || payload;
              
              // Log the full payload to help debug
              console.log('Full token payload:', payload);
              
              // Extract role information from wherever it might be in the token
              // This is a more comprehensive approach to find the role
              if (!userData.role) {
                // Check various possible locations of role data
                if (payload.role) {
                  userData.role = payload.role;
                } else if (payload.user && payload.user.role) {
                  userData.role = payload.user.role;
                } else if (payload.email && payload.email === 'admin@example.com') {
                  // Hard-code known admin credentials as a last resort
                  userData.role = 'admin';
                  console.log('Admin role assigned based on email match');
                } else if (typeof userData === 'string') {
                  // If userData is just an ID string, create a proper object
                  try {
                    const userId = userData;
                    userData = { id: userId, role: 'user' }; // Default to user role
                    // If we can find role elsewhere in payload
                    if (payload.roles && payload.roles.length > 0) {
                      userData.role = payload.roles[0];
                    }
                  } catch (e) {
                    console.error('Error processing user data:', e);
                  }
                }
                
                // Emergency fallback - check if email exists in userData
                if (!userData.role && userData.email && userData.email === 'admin@example.com') {
                  userData.role = 'admin';
                  console.log('Admin role assigned based on email match (fallback)');
                }
              }
              
              console.log('User data from token:', userData);
              setUser(userData);
              setToken(storedToken);
            }
          } else {
            // Not a valid JWT token
            localStorage.removeItem('token');
            setUser(null);
            setToken(null);
          }
        } catch (error) {
          console.error('Error parsing token:', error);
          localStorage.removeItem('token');
          setUser(null);
          setToken(null);
        }
      }
      
      setLoading(false);
    };
    
    verifyToken();
  }, []);
  
  // Login user
  const login = (userData, authToken) => {
    setUser(userData);
    setToken(authToken);
    localStorage.setItem('token', authToken);
  };
  
  // Logout user
  const logout = async () => {
    try {
      if (token) {
        await AuthService.logout(token);
      }
    } catch (error) {
      console.error('Error during logout:', error);
    } finally {
      setUser(null);
      setToken(null);
      localStorage.removeItem('token');
      toast.info('Logged out successfully');
    }
  };
  
  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        login,
        logout,
        token,
        loading
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
